import { FormGroup } from '@angular/forms';

export type EnrollmentScenarioType =
  | 'Pre-Delivery'
  | 'New enrollment'
  | 'Transfer';

export enum EnrollmentScenarioEnum {
  pre = 'Pre-Delivery',
  new = 'New enrollment',
  transfer = 'Transfer',
}

export type FormStep =
  | AircraftInformationDTO
  | AircraftMissionEnrollmentFormDTO
  | EngineInformationDTO
  | CompanyDTO;

export interface UploadedFile {
  name: string;
  source: string;
  nameForUpload?: string;
}

export interface AttachmentsMetadata {
  espReferenceId: string;
  group: string;
}
export interface AttachmentFile {
  name: string;
  content: string;
  metadata: AttachmentsMetadata;
}

export interface AttachmentsDTO {
  filename: string;
  id: number;
  nameForUpload?: string;
}

export interface AttachmentsForSaveDTO {
  filename: string;
  fileRequestId: string;
}

// step 1
export interface AircraftModelDTO {
  aircraftModel: string;
  aircraftEngineNumbers: number;
  engineModels: EngineDTO[];
}

export interface AircraftInformationForm {
  aircraftModel: string;
  aircraftRegistration: string | null;
  aircraftSerialNumber: string;
  aircraftTtsn: string | null;
  aircraftLandings: string | null;
  enrollmentScenario: EnrollmentScenarioType;
  espAgreementNumber?: string;
}

export interface AircraftInformationDTO {
  aircraftModel: string;
  aircraftRegistration: string | null;
  aircraftSerialNumber: string;
  aircraftTtsn: number | null;
  aircraftLandings: number | null;
  enrollmentScenario: EnrollmentScenarioType;
  espAgreementNumber?: string;
}

export interface AircraftDTO {
  aircraftModel: string;
  aircraftSerialNumber: string;
  aircraftRegistration: string;
  aircraftEngineNumbers: number;
  aircraftEquipmentId: string;
  engines: EngineDTO[];
  aircraftLandings?: number;
  aircraftTtsn?: number;
}

export interface EngineDTO {
  engineModel: string;
  engineSerialNumber: string | null;
  enginePosition: string | null;
  engineFamily: string;
  engineTtsn?: number | null;
  engineTcsn?: number | null;
  engineTbo: number | null;
  engineTboOverwritten?: boolean;
  engineWarrantyDate?: string | null;
  engineWarrantyDateUpdated: string | null;
  engineEquipmentId?: string | null;
  coverageLevels?: string[];
}

export interface EngineForDisplay extends EngineDTO {
  tboInitialValue: number | null;
  files: AttachmentsDTO[];
  isFilesFieldValid: boolean;
}

export interface AircraftEnginesDTO {
  engineModel: string;
  engineFamily: string;
  coverageLevel: string[];
}

export interface FormEngines {
  model: string;
  engines: EngineDTO[];
}

// step 2
export interface OperationType {
  name: string;
  isMilitary: boolean;
}

interface AircraftMissionFieldsDto {
  isEquippedWithWeapons: null | boolean;
  isFlownInConflictOrWar: null | boolean;
  isFlownByMilitaryPilots: null | boolean;
  isFlownByMilitaryPilotsConfirmation: null | boolean;
  isMaintainedByMilitaryMechanics: null | boolean;
  isMaintainedByMilitaryMechanicsConfirmation: null | boolean;
  respectCivilLimitations: null | boolean;
}

interface SharedAircraftMissionParams {
  requesterBadgeNumber: string;
  customerCode: string;
  requesterName: string;
  requesterEmail: string;
  requesterCompanyName: string;
}

export interface AircraftMissionEnrollmentFormDTO
  extends SharedAircraftMissionParams {
  aircraftOperation: string;
  militaryUsage: boolean;
  aircraftMission: AircraftMissionFieldsDto;
  aircraftEstimatedHoursPerYear: number | null;
  aircraftEstimatedCyclesPerYear: number | null;
}

export interface AircraftMissionDisplayDTO
  extends AircraftMissionFieldsDto,
    SharedAircraftMissionParams {
  aircraftOperation: OperationType;
  militaryConfirmation: boolean | undefined;
  aircraftEstimatedHoursPerYear: string | null;
  aircraftEstimatedCyclesPerYear: string | null;
}

type MaintenanceProgram = 'FIMP' | 'OCMP';
// step 3
export interface EngineInformationDTO {
  maintenanceProgram: MaintenanceProgram;
  engines: EngineDTO[];
  attachments: AttachmentsDTO[] | null;
  requesterBadgeNumber: string;
  customerCode: string;
  requesterName: string;
  requesterEmail: string;
  requesterCompanyName: string;
}

export interface EngineInformationForm {
  maintenanceProgram: MaintenanceProgram;
  engineModel?: string;
  engines: EngineForDisplay[];
  requesterBadgeNumber: string;
  customerCode: string;
  requesterName: string;
  requesterEmail: string;
  requesterCompanyName: string;
  files: AttachmentsDTO[] | null;
}

//  step 4
export interface CoverageLevelForm {
  coverageLevel: string;
}

// step 5
export enum CompanyRole {
  Contractee = 'Contractee',
  ManagementCompany = 'Management Company',
  EndUser = 'End User',
  RegisteredOwner = 'Registered Owner',
  InterestHolder = 'Aircraft Interest Holder Company',
  Payer = 'Payer',
  RequestorCompany = 'Requestor Company',
}

export enum ContactType {
  Contractee = 'contractee',
  ManagedCompany = 'managedCompany',
  EndUser = 'endUser',
  RegisteredAircraftOwner = 'registeredAircraftOwner',
  AircraftInterestHolder = 'aircraftInterestHolder',
  Payer = 'payer',
  AgreementHolder = 'agreementHolder',
}

export enum CompanyFunction {
  Contractee = 'Contractee',
  ManagedCompany = 'Management Company',
  EndUser = 'End User',
  RegisteredAircraftOwner = 'Registered Owner',
  AircraftInterestHolder = 'Aircraft Interest Holder (AIH)',
  Payer = 'Payer',
  AgreementHolder = 'Agreement Holder',
}

export interface CountryDTO {
  name: string;
  code: string;
  isGrey: boolean;
  regions: string[];
}

export interface CompanyDTO {
  companyRole: string;
  accountNumber: string | null;
  companyName: string | null;
  country: string | null;
  countryCode: string | null;
  streetAddress: string | null;
  city: string | null;
  postalCode: string | null;
  region: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactPhoneNumber: string | null;
  contactEmail: string | null;
  attachments: AttachmentsDTO[] | null;
}

export interface ContactDTO {
  accountNumber: string;
  companyFunction: string;
  companyRole: string;
  city: string;
  companyName: string;
  contactNumber: string;
  contactEmail: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhoneNumber: string;
  contactTitle: string;
  contactType: string;
  country: string;
  countryName: string;
  federalTaxId: string | null;
  region: string;
  streetAddress: string;
  postalCode: string;
  attachments: AttachmentsDTO[] | null;
  isPresent: boolean | null;
}

export interface CompanyForDisplay {
  companyRole: ContactType;
  companyName: AccountDTO | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  region: string | null;
  country: CountryDTO | null;
  principalFirstName: string | null;
  principalLastName: string | null;
  principalPhone: string | null;
  principalEmail: string | null;
  selectedCompany: string | null;
  isPresent?: boolean;
  files?: File[] | null;
  isFilesFieldValid?: boolean;
  phoneDialingCode?: PhoneNumberData;
}

export interface AccountDTO {
  accountNumber: string;
  companyName: string;
  streetAddress: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  contacts: Contact[] | null;
}

export interface Contact {
  contactNumber: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactPhoneNumber: string | null;
  contactEmail: string | null;
  badgeNumber: string | null;
  phoneDialingCode?: PhoneNumberData;
}

export interface SubstepperFormControls {
  contractee: FormGroup;
  managementCompany: FormGroup;
  endUser: FormGroup;
  payer: FormGroup;
  registeredOwner: FormGroup;
  interestHolder: FormGroup;
}

export interface MappedFiles {
  domain: string;
  attachments: Attachments[];
}

interface Attachments {
  filename: string;
  content: string;
  metadata: {
    espRequestID: string;
    category: string;
  };
}

export interface PhoneNumberData {
  countryCode: string;
  countryName: string;
  dialingCode: string;
}

export interface FileStatuses extends AttachmentsDTO {
  status: string;
}

export interface FileStatusDTO {
  errorMessage: string;
  success: true;
  timeOver: true;
  notEveryFileFound: true;
  fileStatuses: FileStatuses[];
}

export interface ESPEnrollmentFormForDisplay {
  aircraftModel: string;
  aircraftLandings: number;
  aircraftRegistration: string;
  aircraftSerialNumber: string;
  aircraftTtsn: number;
  espAgreementNumber: string | null;
  aircraftEngineNumbers: string;

  aircraftOperation: string | null;
  militaryUsage: boolean | null | undefined;
  aircraftEstimatedHoursPerYear: number | null;
  aircraftEstimatedCyclesPerYear: number | null;
  aircraftMission: AircraftMissionFieldsDto;

  engines: EngineDTO[];
  coverageLevel: string;
  contacts: ContactDTO[];
  warrantyAttachments: AttachmentsDTO[] | null;
}
export interface EspEnrollmentFormDto extends ESPEnrollmentFormForDisplay {
  requesterBadgeNumber: string;
  requestInstanceNumber: string;
  customerCode: string;
  enrollmentScenario: EnrollmentScenarioType;
  espAgreementNumber: string;
  maintenanceProgram: string;
  isInterestedInCarbonOffset: boolean;
  requesterName: string;
  requesterEmail: string;
  aircraftEquipmentId: string;
  contract?: ContractDTO;
  requesterCompanyName: string;
}

export interface ContractDTO {
  contractNumber: string;
  contractStatus: string;
  aircraft?: ContractAircraftDTO;
  engines: ContractEngineDTO[];
  accounts: ContractAccountDTO[];
}

export interface ContractAircraftDTO {
  aircraftModel: string;
  aircraftSerialNumber: string;
  aircraftRegistration: string;
}

export interface ContractEngineDTO {
  engineModel: string;
  engineSerialNumber: string;
  coverageLevel: string;
}

export interface ContractAccountDTO {
  accountNumber: string;
  companyRole: string;
  companyName: string;
}

export interface ReviewPdfDTO {
  pdfFile: string;
}

export enum FileStatus {
  Clean = 'Clean',
  Infected = 'Infected',
}
